import { UfoAdventureGame } from '../templates/ufo-adventure/UfoAdventureGame';
import { LaneRallyGame } from '../templates/lane-rally/LaneRallyGame';
import { StationRunGame } from '../templates/station-run/StationRunGame';
import { draftToStationRunConfig } from '../templates/station-run/domain/stationLevels';
import { draftToRallyConfig } from '../templates/lane-rally/domain/rallyLevels';
import { draftToUfoConfig } from '../templates/ufo-adventure/domain/ufoLevels';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type { PublishedGame } from '../../shared/publications';
import type { PublicLeaderboardEntry } from '../../shared/results';
import { balanceRepeatStatus } from '../../shared/balanceRounds';
import { ApiRequestError } from '../auth/authApi';
import type { TrainGameResult } from '../game/EventBus';
import { draftToTrainLevels } from '../game/domain/draftToTrainLevels';
import { PhaserGame } from '../game/PhaserGame';
import { SiteFooter } from '../legal/LegalPages';
import { templateName } from '../../shared/templates';
import { AlevliRotaGame } from '../templates/fiery-route/AlevliRotaGame';
import { fieryRouteModeOf } from '../../shared/fieryRouteModes';
import { draftToFieryRouteLevels } from '../templates/fiery-route/domain/routeLevels';
import { OrbitalBridgeGame } from '../templates/orbital-bridge/OrbitalBridgeGame';
import { draftToOrbitalBridgeConfig } from '../templates/orbital-bridge/domain/bridgeLevels';
import { TurboDuelGame } from '../templates/turbo-duel/TurboDuelGame';
import { draftToTurboDuelConfig } from '../templates/turbo-duel/domain/raceLevels';
import { BalanceArenaGame } from '../templates/balance-arena/BalanceArenaGame';
import { draftToBalanceArenaConfig } from '../templates/balance-arena/domain/balanceLevels';
import { BasketLearningGame } from '../templates/basket-learning/BasketLearningGame';
import { draftToBasketLearningConfig } from '../templates/basket-learning/domain/basketLevels';
import { GoalArenaGame } from '../templates/goal-arena/GoalArenaGame';
import { draftToGoalArenaConfig } from '../templates/goal-arena/domain/goalLevels';
import { BillionaireQuizGame } from '../templates/billionaire-quiz/BillionaireQuizGame';
import { draftToBillionaireQuizConfig } from '../templates/billionaire-quiz/domain/billionaireLevels';
import { formatTurkishLira } from '../../shared/billionaire';
import {
  finishPlayAttempt,
  getLeaderboard,
  getPublishedGame,
  startPlayAttempt,
} from './publicationApi';

type PublishedGamePageProps = { slug: string };
type PlayState = 'ready' | 'starting' | 'playing' | 'saving' | 'finished';

function formatDuration(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function PublishedGamePage({ slug }: PublishedGamePageProps) {
  const [game, setGame] = useState<PublishedGame | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [playState, setPlayState] = useState<PlayState>('ready');
  const [runKey, setRunKey] = useState(0);
  const [attemptId, setAttemptId] = useState('');
  const [message, setMessage] = useState('');
  const [leaderboard, setLeaderboard] = useState<PublicLeaderboardEntry[]>([]);
  const [savedResult, setSavedResult] = useState<{
    score: number;
    originalScore?: number;
    durationMs: number;
    completed: boolean;
    /** Denge tekrar modu: durum metni için cevap sayısı ve düşülen soru. */
    balanceRepeat?: { answered: number; fellAtQuestion: number | null };
  } | null>(null);

  const refreshLeaderboard = async () => {
    try {
      setLeaderboard(await getLeaderboard(slug));
    } catch {
      setLeaderboard([]);
    }
  };

  useEffect(() => {
    const robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]') ?? document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow, noarchive';
    if (!robots.parentNode) document.head.appendChild(robots);
    getPublishedGame(slug)
      .then((published) => {
        setGame(published);
        document.title = `${published.title} — Oyunla Ödevlendir`;
        void refreshLeaderboard();
      })
      .catch((error) => setNotFound(error instanceof ApiRequestError && error.status === 404));
  }, [slug]);

  const levels = useMemo(() => game && game.templateKey === 'train-adventure' ? draftToTrainLevels(game) : null, [game]);
  const routeLevels = useMemo(() => game && game.templateKey === 'fiery-route' ? draftToFieryRouteLevels(game) : null, [game]);
  const bridgeConfig = useMemo(() => game && game.templateKey === 'orbital-bridge' ? draftToOrbitalBridgeConfig(game) : null, [game]);
  const raceConfig = useMemo(() => game && game.templateKey === 'turbo-duel' ? draftToTurboDuelConfig(game) : null, [game]);
  const balanceConfig = useMemo(() => game && game.templateKey === 'balance-arena' ? draftToBalanceArenaConfig(game) : null, [game]);
  const basketConfig = useMemo(() => game && game.templateKey === 'basket-learning' ? draftToBasketLearningConfig(game) : null, [game]);
  const ufoConfig = useMemo(() => game?.templateKey === 'ufo-adventure' ? draftToUfoConfig(game) : null, [game]);
  const rallyConfig = useMemo(() => game?.templateKey === 'lane-rally' ? draftToRallyConfig(game) : null, [game]);
  const stationConfig = useMemo(() => game?.templateKey === 'station-run' ? draftToStationRunConfig(game) : null, [game]);
  const goalConfig = useMemo(() => game && game.templateKey === 'goal-arena' ? draftToGoalArenaConfig(game) : null, [game]);
  const billionaireConfig = useMemo(() => game && game.templateKey === 'billionaire-quiz' ? draftToBillionaireQuizConfig(game) : null, [game]);

  const beginAttempt = async (event?: FormEvent) => {
    event?.preventDefault();
    setPlayState('starting');
    setMessage('Oyun hazırlanıyor…');
    try {
      const current = await getPublishedGame(slug);
      const attempt = await startPlayAttempt(slug, { firstName, lastName, publicationRevision: current.revision ?? 1 });
      setGame(attempt.game);
      setAttemptId(attempt.id);
      setSavedResult(null);
      setRunKey((current) => current + 1);
      setPlayState('playing');
      setMessage('');
    } catch (error) {
      setPlayState('ready');
      setMessage(error instanceof ApiRequestError ? error.message : 'Oyun başlatılamadı.');
    }
  };

  const saveResult = async (result: TrainGameResult['result']) => {
    setPlayState('saving');
    setMessage('Sonucun güvenli biçimde kaydediliyor…');
    try {
      const saved = await finishPlayAttempt(slug, result);
      const balance = result.details?.templateKey === 'balance-arena' && result.details.repeatUntilFall ? result.details : undefined;
      setSavedResult({ ...saved, ...(saved.score !== result.score ? { originalScore: result.score } : {}),
        ...(balance ? { balanceRepeat: { answered: balance.questions.length, fellAtQuestion: balance.fellAtQuestion } } : {}) });
      setPlayState('finished');
      setMessage(saved.completed ? 'Tebrikler, sonucun kaydedildi!' : 'Denemen kaydedildi. Tekrar deneyebilirsin.');
      await refreshLeaderboard();
    } catch (error) {
      setPlayState('finished');
      setMessage(error instanceof ApiRequestError ? error.message : 'Sonuç kaydedilemedi.');
    }
  };

  if (notFound) {
    return (
      <main className="public-game-state">
        <p className="eyebrow">404</p>
        <h1>Sayfa bulunamadı</h1>
        <p>Bu bağlantı mevcut değil.</p>
        <a className="ghost-button" href="/odevlendir/">Oyun merkezine dön</a>
      </main>
    );
  }
  if (!game || (!levels && !routeLevels && !bridgeConfig && !raceConfig && !balanceConfig && !basketConfig && !goalConfig && !billionaireConfig && !ufoConfig && !rallyConfig && !stationConfig)) return <main className="public-game-state"><p>Oyun hazırlanıyor…</p></main>;

  return (
    <div className="site-shell public-game-page">
      <header className="topbar">
        <a className="brand" href="/odevlendir/" aria-label="Oyunla Ödevlendir ana sayfa">
          <span className="brand-mark" aria-hidden="true">
            <img src="/odevlendir/assets/brand/tekk-logo.jpg" alt="" />
          </span>
          <span><strong>Oyunla Ödevlendir</strong><small>{templateName(game.templateKey)}</small></span>
        </a>
      </header>
      <main>
        <section className="game-heading" aria-labelledby="published-game-title">
          <div>
            <p className="eyebrow">Bağlantıya özel etkinlik</p>
            <h1 id="published-game-title">{game.title}</h1>
            <p>{game.templateKey === 'station-run' ? 'Raylarda koş, engellerden kaç, altınları topla ve her istasyonda soruyu cevaplayarak son istasyona ulaş.' : game.templateKey === 'lane-rally' ? 'Gaz pedalına bas, cevap tabelalarını oku ve doğru cevabın şeridine geçerek bitiş çizgisine ulaş.' : game.templateKey === 'ufo-adventure' ? 'UFO’nun rotasını seç, doğru cevap geçidinden uç ve keşif yolculuğunu tamamla.' : game.templateKey === 'fiery-route'
              ? 'Kıvıl ile Çiselerden kaç, doğru cevap geçidini bul ve rotayı tamamla.'
              : game.templateKey === 'orbital-bridge'
                ? 'Doğru enerji modülünü seç, köprüyü tamamla ve keşif aracını yeni dünyalara ulaştır.'
                : game.templateKey === 'turbo-duel'
                  ? 'Soruları cevapla, turboyu ateşle ve iki rakibini bitiş çizgisinde geride bırak.'
                  : game.templateKey === 'balance-arena'
                    ? 'Soruları cevapla, rüzgâra karşı dengede kal ve gökyüzü arenasından düşmeden oyunu tamamla.'
                  : game.templateKey === 'basket-learning'
                    ? 'Soruları cevapla, 3B atışlarını potaya gönder ve seri basketlerle salon finalini tamamla.'
                  : game.templateKey === 'goal-arena'
                    ? 'Soruları cevapla, topun arkasından kaleye bak ve kaleciyi geçen şutlarla golü bul.'
                  : game.templateKey === 'billionaire-quiz'
                    ? 'Dört seçenekli soruları cevapla, kasanı büyüt ve sürpriz bonus kartlarıyla final ödülüne ulaş.'
                : 'Doğru balonu doğru vagona indir ve öğrenme yolculuğunu tamamla.'}</p>
          </div>
          <div className="game-badges">
            <span>{game.maxLevels} {['orbital-bridge', 'turbo-duel', 'balance-arena', 'basket-learning', 'goal-arena', 'billionaire-quiz', 'ufo-adventure', 'lane-rally', 'station-run'].includes(game.templateKey) ? 'soru' : 'seviye'}</span>
            {game.templateKey === 'station-run' ? <span>3B koşu · 4 can</span> : game.templateKey === 'lane-rally' ? <span>3B otoyol · Can yok</span> : game.templateKey === 'ufo-adventure' ? <span>{game.timingMode === 'none' ? 'Seçim bekleyen uçuş' : 'Otomatik uçuş'} · Can yok</span> : game.templateKey === 'balance-arena'
              ? <span>{game.repeatUntilFall ? 'Düşene kadar tekrar · En fazla 5 dk' : 'Tek düşüş'}</span>
              : game.templateKey === 'basket-learning'
              ? <span>3B arena</span>
              : game.templateKey === 'goal-arena'
              ? <span>3B stadyum</span>
              : game.templateKey === 'billionaire-quiz'
              ? <span>Bonus turları</span>
              : game.templateKey === 'turbo-duel'
              ? <span>3 araç</span>
              : game.templateKey === 'fiery-route' && game.bridgeMode === 'guided'
              ? <span>Normal mod · Can yok</span>
              : game.templateKey !== 'orbital-bridge' || game.bridgeMode === 'challenge'
              ? <span>3 can</span>
              : <span>Rehberli mod</span>}
          </div>
        </section>

        {playState === 'ready' || playState === 'starting' ? (
          <section className="player-gate" aria-labelledby="player-gate-title">
            <div>
              <p className="eyebrow">Yolculuğa hazırlan</p>
              <h2 id="player-gate-title">Adını ve soyadını gir</h2>
              <p>Sonucun öğretmenin tarafından görülebilmesi için iki alan da zorunludur.</p>
              <p className="player-privacy-copy">
                Adın ve oyun sonucun öğretmeninle paylaşılır; kamuya açık tabloda soyadının yalnız baş harfi gösterilir.
                Ayrıntılar için <a href="/odevlendir/kvkk" target="_blank" rel="noreferrer">öğrenci bilgilendirmesini</a> inceleyebilirsin.
              </p>
            </div>
            <form onSubmit={(event) => void beginAttempt(event)}>
              <label>Ad<input value={firstName} onChange={(event) => setFirstName(event.target.value)} autoComplete="given-name" maxLength={50} required /></label>
              <label>Soyad<input value={lastName} onChange={(event) => setLastName(event.target.value)} autoComplete="family-name" maxLength={50} required /></label>
              <button className="primary-button" type="submit" disabled={playState === 'starting'}>
                {playState === 'starting' ? 'Hazırlanıyor…' : 'Oyuna başla'}
              </button>
            </form>
            {message && <p className="form-message" role="alert">{message}</p>}
          </section>
        ) : (
          <>
            <section className="play-card" aria-label={`${game.title} oyun alanı`}>
              {game.templateKey === 'station-run' && stationConfig ? (
                <StationRunGame key={runKey} config={stationConfig} label={game.title} restartEnabled={false} onFinished={result => void saveResult(result)} />
              ) : game.templateKey === 'lane-rally' && rallyConfig ? (
                <LaneRallyGame key={runKey} config={rallyConfig} label={game.title} restartEnabled={false} onFinished={result => void saveResult(result)} />
              ) : game.templateKey === 'ufo-adventure' && ufoConfig ? (
                <UfoAdventureGame key={runKey} config={ufoConfig} label={game.title} restartEnabled={false} onFinished={result => void saveResult(result)} />
              ) : game.templateKey === 'billionaire-quiz' && billionaireConfig ? (
                <BillionaireQuizGame key={runKey} config={billionaireConfig} attemptId={attemptId} label={game.title} restartEnabled={false} onFinished={(result) => void saveResult(result)} />
              ) : game.templateKey === 'goal-arena' && goalConfig ? (
                <GoalArenaGame key={runKey} config={goalConfig} label={game.title} restartEnabled={false} onFinished={(result) => void saveResult(result)} />
              ) : game.templateKey === 'basket-learning' && basketConfig ? (
                <BasketLearningGame key={runKey} config={basketConfig} label={game.title} restartEnabled={false} onFinished={(result) => void saveResult(result)} />
              ) : game.templateKey === 'balance-arena' && balanceConfig ? (
                <BalanceArenaGame key={runKey} config={balanceConfig} label={game.title} restartEnabled={false} onFinished={(result) => void saveResult(result)} />
              ) : game.templateKey === 'turbo-duel' && raceConfig ? (
                <TurboDuelGame
                  key={runKey}
                  config={raceConfig}
                  label={game.title}
                  restartEnabled={false}
                  onFinished={(result) => void saveResult(result)}
                />
              ) : game.templateKey === 'orbital-bridge' && bridgeConfig ? (
                <OrbitalBridgeGame
                  key={runKey}
                  config={bridgeConfig}
                  label={game.title}
                  restartEnabled={false}
                  onFinished={(result) => void saveResult(result)}
                />
              ) : game.templateKey === 'fiery-route' && routeLevels ? (
                <AlevliRotaGame
                  key={runKey}
                  levels={routeLevels}
                  mode={fieryRouteModeOf(game.bridgeMode)}
                  label={game.title}
                  restartEnabled={false}
                  onFinished={(result) => void saveResult(result)}
                />
              ) : levels ? (
                <PhaserGame
                  key={runKey}
                  levels={levels}
                  label={game.title}
                  restartEnabled={false}
                  onFinished={(result) => void saveResult(result)}
                />
              ) : null}
              <p className="orientation-tip">Daha geniş bir görünüm için telefonunu yatay çevirebilirsin.</p>
            </section>
            {(playState === 'saving' || playState === 'finished') && (
              <section className="result-status" aria-live="polite">
                <div>
                  <p className="eyebrow">Oyun sonucu</p>
                  <h2>{message}</h2>
                  {savedResult && (
                    <p>{game.templateKey === 'billionaire-quiz' ? formatTurkishLira(savedResult.score) : `${savedResult.score} puan`} · {formatDuration(savedResult.durationMs)} · {savedResult.balanceRepeat ? balanceRepeatStatus({ completed: savedResult.completed, ...savedResult.balanceRepeat, questionCount: game.maxLevels }) : savedResult.completed ? 'Tamamlandı' : 'Tamamlanamadı'}</p>
                  )}
                  {savedResult?.originalScore !== undefined && <p>Öğretmenin cevap anahtarını güncelledi. Kaydedilen puanın cevaplarına göre düzeltildi:
                    {' '}{game.templateKey === 'billionaire-quiz' ? formatTurkishLira(savedResult.originalScore) : savedResult.originalScore}
                    {' → '}{game.templateKey === 'billionaire-quiz' ? formatTurkishLira(savedResult.score) : `${savedResult.score} puan`}.</p>}
                </div>
                {playState === 'finished' && (
                  <button className="primary-button" type="button" onClick={() => void beginAttempt()}>Tekrar oyna</button>
                )}
              </section>
            )}
          </>
        )}

        <section className="leaderboard" aria-labelledby="leaders-title">
          <div>
            <p className="eyebrow">İlk 10</p>
            <h2 id="leaders-title">{game.templateKey === 'station-run' ? 'En İyi Koşucular' : game.templateKey === 'lane-rally' ? 'En Hızlı Pilotlar' : game.templateKey === 'ufo-adventure' ? 'En İyi Pilotlar' : game.templateKey === 'billionaire-quiz' ? 'En Büyük Kasalar' : game.templateKey === 'goal-arena' ? 'Gol Kralları' : game.templateKey === 'basket-learning' ? 'Potanın Yıldızları' : game.templateKey === 'balance-arena' ? 'En Dayanıklı Oyuncular' : game.templateKey === 'turbo-duel' ? 'En Hızlı Yarışçılar' : game.templateKey === 'fiery-route' || game.templateKey === 'orbital-bridge' ? 'En İyi Kaşifler' : 'En İyi Yolcular'}</h2>
          </div>
          {leaderboard.length === 0 ? (
            <p className="empty-leaderboard">Henüz tamamlanmış bir oyun yok. İlk yolcu sen olabilirsin!</p>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Sıra</th><th>Oyuncu</th><th>Durum</th><th>{game.templateKey === 'billionaire-quiz' ? 'Kazanç' : 'Puan'}</th><th>Süre</th></tr></thead>
                <tbody>
                  {leaderboard.map((entry) => (
                    <tr key={`${entry.rank}-${entry.displayName}-${entry.score}`}>
                      <td>{entry.rank}</td><td>{entry.displayName}<small className="result-version">Oynanan sürüm {entry.publicationRevision ?? 1}{entry.gradingRevision !== entry.publicationRevision ? ` · Değerlendirme ${entry.gradingRevision ?? 1}` : ''}</small></td>
                      <td>{entry.balanceRepeat ? `${balanceRepeatStatus({ completed: entry.completed, ...entry.balanceRepeat, questionCount: entry.totalQuestions })} · ${entry.correct} doğru` : entry.completed ? 'Tamamladı' : `Tamamlanamadı · ${entry.correct}/${entry.totalQuestions}`}</td>
                      <td>{game.templateKey === 'billionaire-quiz' ? formatTurkishLira(entry.score) : entry.score}</td><td>{formatDuration(entry.durationMs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="privacy-note">Kamuya açık tabloda soyadlar yalnızca baş harfiyle gösterilir.</p>
          {(game.revision ?? 1) > 1 && <p className="privacy-note">Öğretmenin içeriği düzeltti. Cansız modlarda geçmiş puanlar güncel cevap anahtarına göre hesaplanır; canlı modlarda (3 veya 4 can) puan korunur.</p>}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
